// NetTV i18n - 17 languages
// Backward-compatible with previous API: I18nProvider, useT, SUPPORTED_LANGS, translate.
// 16 new languages were imported from the DonTV bootstrap endpoint (/api/iptv_api.php)
// and mapped onto our existing NetTV UI literals. Any literal without a DonTV equivalent
// falls back to English so nothing ever appears blank.
import React, { createContext, useContext, useEffect, useState } from "react";

import ar from "./locales/ar.json";
import bg from "./locales/bg.json";
import da from "./locales/da.json";
import de from "./locales/de.json";
import el from "./locales/el.json";
import en from "./locales/en.json";
import es from "./locales/es.json";
import fr from "./locales/fr.json";
import hi from "./locales/hi.json";
import id from "./locales/id.json";
import it from "./locales/it.json";
import nl from "./locales/nl.json";
import pl from "./locales/pl.json";
import pt from "./locales/pt.json";
import sl from "./locales/sl.json";
import tr from "./locales/tr.json";
import uk from "./locales/uk.json";

const STORAGE_KEY = "nettv_lang";
const DEFAULT_LANG = "en";

const TRANSLATIONS = { ar, bg, da, de, el, en, es, fr, hi, id, it, nl, pl, pt, sl, tr, uk };

export const SUPPORTED_LANGS = [
  { code: "en", name: "English",    flag: "\uD83C\uDDEC\uD83C\uDDE7" },
  { code: "fr", name: "Fran\u00E7ais",   flag: "\uD83C\uDDEB\uD83C\uDDF7" },
  { code: "de", name: "Deutsch",    flag: "\uD83C\uDDE9\uD83C\uDDEA" },
  { code: "es", name: "Espa\u00F1ol",    flag: "\uD83C\uDDEA\uD83C\uDDF8" },
  { code: "it", name: "Italiano",   flag: "\uD83C\uDDEE\uD83C\uDDF9" },
  { code: "pt", name: "Portugu\u00EAs",  flag: "\uD83C\uDDF5\uD83C\uDDF9" },
  { code: "nl", name: "Nederlands", flag: "\uD83C\uDDF3\uD83C\uDDF1" },
  { code: "pl", name: "Polski",     flag: "\uD83C\uDDF5\uD83C\uDDF1" },
  { code: "tr", name: "T\u00FCrk\u00E7e",     flag: "\uD83C\uDDF9\uD83C\uDDF7" },
  { code: "ar", name: "\u0627\u0644\u0639\u0631\u0628\u064A\u0629",    flag: "\uD83C\uDDF8\uD83C\uDDE6" },
  { code: "el", name: "\u0395\u03BB\u03BB\u03B7\u03BD\u03B9\u03BA\u03AC",   flag: "\uD83C\uDDEC\uD83C\uDDF7" },
  { code: "da", name: "Dansk",      flag: "\uD83C\uDDE9\uD83C\uDDF0" },
  { code: "bg", name: "\u0411\u044A\u043B\u0433\u0430\u0440\u0441\u043A\u0438",  flag: "\uD83C\uDDE7\uD83C\uDDEC" },
  { code: "uk", name: "\u0423\u043A\u0440\u0430\u0457\u043D\u0441\u044C\u043A\u0430", flag: "\uD83C\uDDFA\uD83C\uDDE6" },
  { code: "hi", name: "\u0939\u093F\u0928\u094D\u0926\u0940",      flag: "\uD83C\uDDEE\uD83C\uDDF3" },
  { code: "id", name: "Indonesia",  flag: "\uD83C\uDDEE\uD83C\uDDE9" },
  { code: "sl", name: "Sloven\u0161\u010Dina",flag: "\uD83C\uDDF8\uD83C\uDDEE" },
];

const RTL_LANGS = new Set(["ar"]);

const I18nContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: (s) => s,
});

function applyDir(lang) {
  try {
    document.documentElement.setAttribute("lang", lang);
    document.documentElement.setAttribute("dir", RTL_LANGS.has(lang) ? "rtl" : "ltr");
  } catch { /* ssr */ }
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || DEFAULT_LANG; }
    catch { return DEFAULT_LANG; }
  });

  const setLang = (code) => {
    try { localStorage.setItem(STORAGE_KEY, code); } catch {}
    setLangState(code);
    applyDir(code);
    window.dispatchEvent(new CustomEvent("nettv-lang-change", { detail: code }));
  };

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY && e.newValue) setLangState(e.newValue);
    };
    window.addEventListener("storage", onStorage);
    applyDir(lang);
    return () => window.removeEventListener("storage", onStorage);
  }, [lang]);

  const t = (key) => {
    if (!key) return key;
    const dict = TRANSLATIONS[lang] || {};
    return dict[key] || key;
  };

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useT() { return useContext(I18nContext); }

export function translate(key) {
  try {
    const lang = localStorage.getItem(STORAGE_KEY) || DEFAULT_LANG;
    const dict = TRANSLATIONS[lang] || {};
    return dict[key] || key;
  } catch {
    return key;
  }
}
